import { observer } from 'mobx-react-lite';
import * as React from 'react';
import loc from 'owa-localize';
import { isEduStudent } from 'owa-bpos-utils';
import {
    removeAccount,
    trueTimeToggle,
    trueTimeToolTip,
    trueTimeToggleEdu,
    trueTimeToolTipForSchool,
    trueTimeDescription,
    trueTimeDescriptionEdu,
} from './ConnectedAccount.locstring.json';
import type { OWAConnectedAccount } from 'owa-accounts-store';
import {
    lazyGetStoreIsSettled,
    lazyAddMergedViewAvailabilityAccount,
    lazyRemoveMergedViewAvailabilityAccount,
} from 'owa-truetime';
import { Label } from '@fluentui/react/lib/Label';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';

import {
    getAccountProviderTypeIcon,
    isTrueTimeEnabledForProvider,
    getAccountAddressForMergedView,
} from 'owa-accounts-utils';
import getAccountPendingTrueTimeUpdate from '../selectors/getAccountPendingTrueTimeUpdate';
import setTrueTimeUpdateFailedAccountMutator from '../mutators/setTrueTimeUpdateFailedAccountMutator';
import setAccountPendingTrueTimeUpdateMutator from '../mutators/setAccountPendingTrueTimeUpdateMutator';
import { logUsage } from 'owa-analytics';
import { startRemovingAccount } from '../actions/internalActions';
import { AccountsEditorState } from '../store/schema/AccountsEditorState';
import setAccountsEditorState from '../mutators/setAccountsEditorState';
import getAccountsEditorSource from '../selectors/getAccountsEditorSource';
import { getAllCalendars } from 'owa-calendar-cache';
import { getUserMailboxInfo } from 'owa-client-ids';
import ConnectedAccountCalendars from './ConnectedAccountCalendars';

const styles = require('./ConnectedAccount.scss');
const accountProviderStyles = require('./AccountProvider.scss');

export interface ConnectedAccountProps {
    account: OWAConnectedAccount;
    mergeViewEnabledAccount: string;
}

export default observer(function ConnectedAccount(props: ConnectedAccountProps) {
    const accountsEditorSource = getAccountsEditorSource();

    React.useEffect(() => {
        if (shouldDisableTrueTimeToggle()) {
            logUsage('TrueTimeServerClientMismatch');
        }
    }, []);
    const shouldDisableTrueTimeToggle = () => {
        return (
            props.mergeViewEnabledAccount &&
            props.mergeViewEnabledAccount != getAccountAddressForMergedView(props.account)
        );
    };
    const removeButtonOnClick = () => {
        const { account, mergeViewEnabledAccount } = props;
        startRemovingAccount(account, mergeViewEnabledAccount);
    };

    const enableTrueTimeToggleOnChange = async (event, checked) => {
        const { account } = props;
        // Clear the error message while we wait for a response
        setTrueTimeUpdateFailedAccountMutator(null);

        // Show the spinner while waiting for a response from the server
        setAccountPendingTrueTimeUpdateMutator(account.userIdentity);
        if (checked == true) {
            setAccountsEditorState(AccountsEditorState.None);
            const addMergedViewAvailabilityAccount = await lazyAddMergedViewAvailabilityAccount.import();
            await addMergedViewAvailabilityAccount(
                account.userIdentity,
                account.accountUniqueId,
                account.accountProviderType,
                accountsEditorSource
            );
        } else {
            setAccountsEditorState(AccountsEditorState.TrueTimeDisabled);
            const removeMergedViewAvailabilityAccount = await lazyRemoveMergedViewAvailabilityAccount.import();
            await removeMergedViewAvailabilityAccount(
                account.accountProviderType,
                accountsEditorSource,
                'User' /* SourceTrigger */
            );
        }
        // Hide the spinner now that we have a response from the server
        setAccountPendingTrueTimeUpdateMutator(null);
    };
    const { account, mergeViewEnabledAccount } = props;
    const getStoreIsSettled = lazyGetStoreIsSettled.tryImportForRender();
    const isTrueTimeStoreSettled = getStoreIsSettled ? getStoreIsSettled() : false;
    const isUpdatePending = getAccountPendingTrueTimeUpdate() == account.userIdentity;
    const showTrueTimeSpinner = !isTrueTimeStoreSettled || isUpdatePending;
    const showTruetimeToggle = isTrueTimeEnabledForProvider(account.accountProviderType);
    const connectedAccountCalendars = getAllCalendars().filter(calendar => {
        const defaultUserId = getUserMailboxInfo().userIdentity;
        return calendar && calendar.id.mailboxInfo.userIdentity != defaultUserId;
    });
    const isTrueTimeEnabled =
        mergeViewEnabledAccount == getAccountAddressForMergedView(props.account);
    return (
        <div className={styles.accountContainer}>
            <div className={styles.accountDetailsContainer}>
                <img
                    className={accountProviderStyles.accountProviderIcon}
                    src={getAccountProviderTypeIcon(account.accountProviderType)}
                />
                <div className={styles.accountDescriptionColumn}>
                    <p className={styles.accountName}>{account.userIdentity}</p>
                </div>
            </div>
            <div className={styles.trueTimeContainer}>
                <div className={styles.trueTimeToggleContainer}>
                    {showTruetimeToggle && (
                        <>
                            {showTrueTimeSpinner ? (
                                <Spinner
                                    styles={{
                                        circle: {
                                            margin: '0 12px 0 8px',
                                        },
                                    }}
                                    size={SpinnerSize.medium}
                                    className={styles.trueTimeToggleSpinner}
                                />
                            ) : (
                                <div className={styles.trueTimeToggleLabel}>
                                    <Toggle
                                        ariaLabel={
                                            isEduStudent()
                                                ? loc(trueTimeToggleEdu) +
                                                  loc(trueTimeToolTipForSchool)
                                                : loc(trueTimeToggle) + loc(trueTimeToolTip)
                                        }
                                        disabled={shouldDisableTrueTimeToggle()}
                                        className={styles.trueTimeToggle}
                                        checked={isTrueTimeEnabled}
                                        onChange={enableTrueTimeToggleOnChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {
                        <div className={styles.trueTimeTooltip}>
                            <Label id="trueTimeLabelId" className={styles.trueTimeLabel}>
                                {loc(trueTimeToggle)}
                            </Label>
                            <TooltipHost
                                hostClassName={styles.trueTimeToolTipHost}
                                content={
                                    isEduStudent()
                                        ? loc(trueTimeToolTipForSchool)
                                        : loc(trueTimeToolTip)
                                }
                                directionalHint={DirectionalHint.rightCenter}></TooltipHost>
                        </div>
                    }
                </div>
                {
                    <div className={styles.trueTimeCalendarList}>
                        <Label className={styles.trueTimeDescription}>
                            {isEduStudent()
                                ? loc(trueTimeDescriptionEdu)
                                : loc(trueTimeDescription)}
                        </Label>
                        <ConnectedAccountCalendars
                            calendars={connectedAccountCalendars}
                            isTrueTimeEnabled={isTrueTimeEnabled}
                            accountProviderType={account.accountProviderType}
                        />
                        <div className={styles.editButtons}>
                            <DefaultButton
                                disabled={isUpdatePending}
                                className={styles.removeAccountButton}
                                text={loc(removeAccount)}
                                onClick={removeButtonOnClick}
                            />
                        </div>
                    </div>
                }
            </div>
        </div>
    );
});
